// @flow

import React, {createContext, useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';
import {useOverlayScrollbars} from 'overlayscrollbars-react';
import {getDefaultOptions} from '@pb/kit-component-scroll';

import type {Node} from 'react';
import type {OverlayScrollbars} from 'overlayscrollbars';
// $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
import type {UseOverlayScrollbarsInstance} from 'overlayscrollbars-react';

const RESIZE_HANDLER_INDENT = 6; // px
type Listener = (instance: OverlayScrollbars, e?: Event) => void;

export type ScrollContextType = {
    getScrollInstance: () => OverlayScrollbars | null,
    scrollXLineRef: { current: HTMLElement | null },
    scrollContainerRef: { current: HTMLElement | null },
    headerScrollContainerRef: { current: HTMLElement | null },
    addListener: (type: string, listener: Listener) => void,
};

export const SCROLL_DEFAULT_CONTEXT: ScrollContextType = {
    getScrollInstance: () => null,
    scrollXLineRef: {current: null},
    scrollContainerRef: {current: null},
    headerScrollContainerRef: {current: null},
    addListener: () => {
    }
};

export const ScrollContext: React$Context<ScrollContextType>
    = createContext<ScrollContextType>(SCROLL_DEFAULT_CONTEXT);
ScrollContext.displayName = 'ScrollContext';

type Props = {|
    children: Node,
    disabledScroll?: boolean,
    hasContent: boolean,
    // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
    scrollRef?: React$Ref<UseOverlayScrollbarsInstance>,
|};

export function ScrollContextProvider(props: Props): Node {
    const {
        children,
        disabledScroll,
        hasContent,
        scrollRef
    } = props;

    const headerScrollContainerRef = useRef<HTMLElement | null>(null);
    const scrollContainerRef = useRef<HTMLElement | null>(null);
    const scrollXLineRef = useRef<HTMLElement | null>(null);
    const listeners = useRef<{ [string]: Listener[] }>(new Map());
    const addListener: $PropertyType<ScrollContextType, 'addListener'> = useCallbackWithRef((type, listener) => {
        if (!listeners.current[type]) {
            listeners.current[type] = [];
        }
        listeners.current[type].push(listener);
    });

    const options = getDefaultOptions();

    const isBodyScrolling = useRef(false);
    const bodyScrollTimeout = useRef<?TimeoutID>(null);
    const isHeaderScrolling = useRef(false);
    const headerScrollTimeout = useRef<?TimeoutID>(null);

    // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
    const syncHeaderScrollPositionWithBody = useCallbackWithRef((self: OverlayScrollbars) => {
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        const {scrollLeft} = self.elements().viewport;
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        instanceHeader() // eslint-disable-line @pb/flowtype/no-use-before-define
            ?.elements().viewport.scrollTo({
            left: scrollLeft,
            behavior: 'instant',
        });
    });

    // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
    const syncBodyScrollPositionWithHeader = useCallbackWithRef((self: OverlayScrollbars) => {
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        const {scrollLeft} = self.elements().viewport;
        // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
        instance() // eslint-disable-line @pb/flowtype/no-use-before-define
            ?.elements().viewport.scrollTo({
            left: scrollLeft,
            behavior: 'instant',
        });
    });

    // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
    const [initialize, instance] = useOverlayScrollbars({
        options: {
            ...options,
            scrollbars: {
                // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
                ...options.scrollbars,
                autoHide: 'never',
                autoHideSuspend: false,
                clickScroll: 'instant',
            },
        },
        events: {
            initialized: self => {
                if (hasContent) {
                    syncHeaderScrollPositionWithBody(self);
                }

                // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
                listeners.current.initialized?.forEach(listener => {
                    listener(self);
                });
            },
            updated: self => {
                if (hasContent) {
                    syncHeaderScrollPositionWithBody(self);
                }

                // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
                listeners.current.updated?.forEach(listener => {
                    listener(self);
                });
            },
            scroll: (self, event) => {
                if (!isHeaderScrolling.current) {
                    isBodyScrolling.current = true;
                    if (bodyScrollTimeout.current) {
                        clearTimeout(bodyScrollTimeout.current);
                    }
                    bodyScrollTimeout.current = setTimeout(() => {
                        isBodyScrolling.current = false;
                    }, 70);
                    if (hasContent) {
                        syncHeaderScrollPositionWithBody(self);
                    }
                }

                // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
                listeners.current.scroll?.forEach(listener => {
                    listener(self, event);
                });
            },
        },
    });

    // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
    const [initializeHeader, instanceHeader] = useOverlayScrollbars({
        options: {
            ...options,
            scrollbars: {
                // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
                ...options.scrollbars,
                visibility: 'hidden',
            },
        },
        events: {
            initialized: self => {
                syncBodyScrollPositionWithHeader(self);
            },
            updated: self => {
                syncBodyScrollPositionWithHeader(self);
            },
            scroll: self => {
                if (!isBodyScrolling.current) {
                    isHeaderScrolling.current = true;
                    if (headerScrollTimeout.current) {
                        clearTimeout(headerScrollTimeout.current);
                    }
                    headerScrollTimeout.current = setTimeout(() => {
                        isHeaderScrolling.current = false;
                    }, 70);

                    // This is necessary to ensure that the resize-handler (last child) does not affect the overall width of the header container
                    const viewport = self.elements().viewport;
                    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth - RESIZE_HANDLER_INDENT;
                    if (viewport.scrollLeft > maxScrollLeft) {
                        viewport.scrollLeft = maxScrollLeft;
                    }

                    syncBodyScrollPositionWithHeader(self);
                }
            },
        },
    });

    useEffect(() => {
        if (!scrollContainerRef.current || !scrollXLineRef.current || disabledScroll) {
            return;
        }

        initialize({
            target: scrollContainerRef.current,
            scrollbars: {
                slot: scrollXLineRef.current,
            },
        });
        return () => {
            // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
            instance()?.destroy();
        };
    }, [instance, initialize, disabledScroll]);

    useEffect(() => {
        if (!headerScrollContainerRef.current || disabledScroll) {
            return;
        }
        initializeHeader(headerScrollContainerRef.current);
        return () => {
            // $FlowFixMe TODO: k.garnaga@spd-ukraine.com - waiting for migration to TS
            instanceHeader()?.destroy();
        };
    }, [initializeHeader, instanceHeader, disabledScroll]);

    useImperativeHandle(scrollRef, () => instance, [instance]);

    const value = useMemo(() => ({
        getScrollInstance: instance,
        scrollXLineRef,
        headerScrollContainerRef,
        addListener,
        scrollContainerRef
    }: ScrollContextType), [
        instance,
        addListener,
    ]);

    return (
        <ScrollContext.Provider value={value}>
            {children}
        </ScrollContext.Provider>
    );
}
